import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as ccStockCloseAPI from '../../api/PurchaseAPI/ccStockCloseAPI';

// Async Thunks
// =============================================

export const fetchCCForCCStockClose = createAsyncThunk(
    'ccStockClose/fetchCCForCCStockClose',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ccStockCloseAPI.getCCForCCStockClose(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Cost Centers');
        }
    }
);

export const checkLastCCStockClose = createAsyncThunk(
    'ccStockClose/checkLastCCStockClose',
    async (ccCode, { rejectWithValue }) => {
        try {
            const response = await ccStockCloseAPI.checkLastCCStockClose(ccCode);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to check last close');
        }
    }
);

export const fetchCCStockAmount = createAsyncThunk(
    'ccStockClose/fetchCCStockAmount',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ccStockCloseAPI.getCCStockAmount(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch stock amounts');
        }
    }
);

export const saveCCStockClose = createAsyncThunk(
    'ccStockClose/saveCCStockClose',
    async (params, { rejectWithValue }) => {
        try {
            const response = await ccStockCloseAPI.saveCCStockClose(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to save Stock Close');
        }
    }
);

// Initial State
// =============
const initialState = {
    costCenters: [],
    lastClose: null, // { CloseExist, FromDate, LastClosingAmount, LastCloseId, PrevCloseDate, CCOpeningDate }
    stockAmount: null, // { TotalPurchaseAmt, NewStockAmt, OldStockAmt, DailyIssueAmt, CurrentStockAmount }
    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'

    loading: {
        costCenters: false,
        lastClose: false,
        stockAmount: false,
        save: false,
    },

    errors: {
        costCenters: null,
        lastClose: null,
        stockAmount: null,
        save: null,
    },
};

const ccStockCloseSlice = createSlice({
    name: 'ccStockClose',
    initialState,
    reducers: {
        clearLastClose: (state) => {
            state.lastClose = null;
            state.errors.lastClose = null;
        },
        clearStockAmount: (state) => {
            state.stockAmount = null;
            state.errors.stockAmount = null;
        },
        clearSaveResult: (state) => {
            state.saveResult = null;
            state.saveStatus = null;
            state.errors.save = null;
        },
        resetCCStockClose: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCCForCCStockClose.pending, (state) => {
                state.loading.costCenters = true;
                state.errors.costCenters = null;
            })
            .addCase(fetchCCForCCStockClose.fulfilled, (state, action) => {
                state.loading.costCenters = false;
                state.costCenters = action.payload?.Data || [];
            })
            .addCase(fetchCCForCCStockClose.rejected, (state, action) => {
                state.loading.costCenters = false;
                state.errors.costCenters = action.payload;
                state.costCenters = [];
            });

        builder
            .addCase(checkLastCCStockClose.pending, (state) => {
                state.loading.lastClose = true;
                state.errors.lastClose = null;
            })
            .addCase(checkLastCCStockClose.fulfilled, (state, action) => {
                state.loading.lastClose = false;
                state.lastClose = action.payload?.Data || null;
            })
            .addCase(checkLastCCStockClose.rejected, (state, action) => {
                state.loading.lastClose = false;
                state.errors.lastClose = action.payload;
                state.lastClose = null;
            });

        builder
            .addCase(fetchCCStockAmount.pending, (state) => {
                state.loading.stockAmount = true;
                state.errors.stockAmount = null;
            })
            .addCase(fetchCCStockAmount.fulfilled, (state, action) => {
                state.loading.stockAmount = false;
                state.stockAmount = action.payload?.Data || null;
            })
            .addCase(fetchCCStockAmount.rejected, (state, action) => {
                state.loading.stockAmount = false;
                state.errors.stockAmount = action.payload;
                state.stockAmount = null;
            });

        builder
            .addCase(saveCCStockClose.pending, (state) => {
                state.loading.save = true;
                state.errors.save = null;
                state.saveStatus = 'pending';
            })
            .addCase(saveCCStockClose.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;

                const responseStr = typeof action.payload?.Data === 'string' ? action.payload.Data : '';

                if (responseStr === 'Submited') {
                    state.saveStatus = 'success';
                } else {
                    state.saveStatus = 'failed';
                    state.errors.save = responseStr || 'Save failed';
                }
            })
            .addCase(saveCCStockClose.rejected, (state, action) => {
                state.loading.save = false;
                state.errors.save = action.payload;
                state.saveStatus = 'failed';
                state.saveResult = null;
            });
    },
});

export const {
    clearLastClose,
    clearStockAmount,
    clearSaveResult,
    resetCCStockClose,
} = ccStockCloseSlice.actions;

// Selectors
// =========
export const selectCostCentersArray = (state) => {
    const cc = state.ccStockClose.costCenters;
    return Array.isArray(cc) ? cc : [];
};
export const selectCostCentersLoading = (state) => state.ccStockClose.loading.costCenters;

export const selectLastClose = (state) => state.ccStockClose.lastClose;
export const selectLastCloseLoading = (state) => state.ccStockClose.loading.lastClose;

export const selectStockAmount = (state) => state.ccStockClose.stockAmount;
export const selectStockAmountLoading = (state) => state.ccStockClose.loading.stockAmount;

export const selectSaveStatus = (state) => state.ccStockClose.saveStatus;
export const selectSaveLoading = (state) => state.ccStockClose.loading.save;
export const selectSaveError = (state) => state.ccStockClose.errors.save;

export default ccStockCloseSlice.reducer;
